import type { SaveAdapter } from './adapters/SaveAdapter';
import { StorageUnavailableError } from './adapters/LocalAdapter';
import { SAVE_VERSION, createInitialSave, type PlayerSave } from './PlayerSave.schema';
import { applyMigrations } from './migrations';

/**
 * Manager save'a — fasada nad adapterem persistence.
 *
 * Lifecycle:
 *   1. `await load()` przy starcie aplikacji (main.tsx, przed createGame)
 *      - storage OK + save istnieje → migracje → zwróć
 *      - storage OK + brak save'a → fresh save z STARTER_SEED + zapisz
 *      - storage broken (`StorageUnavailableError`) → fresh save IN-MEMORY
 *        ONLY (NIE próbujemy zapisać, żeby nie nadpisać istniejącego save'a
 *        przy chwilowym IDB outage)
 *   2. `getCurrent()` synchroniczny accessor (Phaser/React mogą wołać)
 *   3. `save(state)` zapisuje + aktualizuje cached `current` (immutable update)
 *   4. `exportJson()` / `importJson()` — ręczny backup gracza
 *
 * NIE TRZYMA autosave logic — to zadanie AutoSaveBridge (sesja 17b).
 */
export class SaveManager {
  private current: PlayerSave | null = null;

  /**
   * Flaga "in-memory only" — true gdy storage nie działa.
   * Save() w tym trybie NIE próbuje disk write (tylko aktualizuje cached),
   * ale `getCurrent()` zwraca fresh save trzymany w pamięci. Gracz może grać,
   * ale stan jest efemeryczny.
   */
  private inMemoryOnly = false;

  /**
   * **Drift-guard (Faza 1b-1, DESIGN_PERFORMANCE_SCALING.md §A3).** Najwyższy
   * `save_rev` jaki klient już zastosował. Każda autorytatywna odpowiedź serwera
   * (akcja ekonomii / world-tick / daily / GET /saves/me) niesie monotoniczny
   * `save_rev`; odpowiedź z `save_rev <= appliedRev` jest IGNOROWANA (stara /
   * duplikat / spóźniona) — chroni waluty i staty przed cofnięciem.
   *
   * Samoinicjuje się na pierwszej zaadoptowanej odpowiedzi (start = 0; pierwszy
   * server-rev > 0 zostaje przyjęty i zapamiętany). W tej rundzie odpowiedzi
   * niosą PEŁNY save, więc adopcja jest samonaprawialna nawet przy luce rev
   * (multi-tab) — pełny resync `GET /saves/me` przy luce dojdzie w rundzie delt.
   */
  private appliedRev = 0;

  constructor(private readonly adapter: SaveAdapter) {}

  async load(): Promise<PlayerSave> {
    let stored: PlayerSave | null;
    try {
      stored = await this.adapter.load();
    } catch (err) {
      if (err instanceof StorageUnavailableError) {
        console.warn('[SaveManager] Storage nieaktywne (incognito/quota), tryb in-memory:', err.message);
        this.inMemoryOnly = true;
        this.current = createInitialSave();
        return this.current;
      }
      throw err;       // inne błędy są nieoczekiwane, propagujemy
    }

    if (stored == null) {
      // Pierwsze uruchomienie albo wymazane IDB
      const fresh = createInitialSave();
      this.current = fresh;
      try {
        await this.adapter.save(fresh);
      } catch (err) {
        if (err instanceof StorageUnavailableError) {
          // LocalAdapter: storage niedostępne (incognito/quota) → in-memory.
          console.warn('[SaveManager] Storage niedostępne przy fresh save, tryb in-memory:', err.message);
          this.inMemoryOnly = true;
        } else {
          // ServerSaveAdapter (sesja 34B HIGH 1 fix): PUT fail (network/401/
          // 400/500) NIE degraduje cicho do in-memory — inaczej gracz grałby
          // na fresh save który NIGDY nie trafił na serwer. Propagujemy →
          // bootstrap error screen.
          console.error('[SaveManager] Zapis początkowego save\'a nieudany:', err);
          throw err;
        }
      }
      console.info('[SaveManager] Utworzono fresh save (pierwsze uruchomienie)');
      return fresh;
    }

    // Migracje jeśli starsza wersja
    let final: PlayerSave;
    if (stored.version < SAVE_VERSION) {
      console.info(`[SaveManager] Migruję save z v${stored.version} → v${SAVE_VERSION}`);
      final = applyMigrations(stored as unknown as Record<string, unknown>);
      try {
        await this.adapter.save(final);
      } catch (err) {
        if (err instanceof StorageUnavailableError) {
          // LocalAdapter: akceptowalne — następny load znów zmigruje.
          console.warn('[SaveManager] Nie udało się zapisać po migracji (storage):', err.message);
        } else {
          // ServerSaveAdapter (sesja 34B HIGH 1 fix): server zapis po migracji
          // musi się udać — inaczej klient ma zmigrowany stan w pamięci a serwer
          // stary. Propagujemy zamiast cicho ignorować.
          console.error('[SaveManager] Zapis po migracji nieudany:', err);
          throw err;
        }
      }
    } else if (stored.version > SAVE_VERSION) {
      console.warn(`[SaveManager] Save w wersji v${stored.version} jest nowszy niż aktualny kod (v${SAVE_VERSION}). Możliwe niezgodności.`);
      final = stored;
    } else {
      final = stored;
    }

    this.current = final;
    // Drift-guard boot-init (Faza 1b-1, MED 1): ustaw `appliedRev` z `save_rev`
    // zwróconego przez `GET /saves/me` (ServerSaveAdapter eksponuje go przez
    // `getLastLoadedRev`). Bez tego konto z historią (rev > 0) startowałoby z
    // appliedRev=0 — pierwsza odpowiedź i tak by się przyjęła, ale jawny init
    // domyka okno i jest wymagany przed rundą delt. LocalAdapter/fresh → 0.
    this.appliedRev = this.adapter.getLastLoadedRev?.() ?? 0;
    console.info(`[SaveManager] Załadowano save z ${new Date(final.lastSavedAt).toISOString()} (save_rev=${this.appliedRev})`);
    return final;
  }

  /**
   * Synchroniczny accessor do aktualnego save'a w pamięci.
   * Wywołuj DOPIERO po `await load()`. Inaczej throws.
   */
  getCurrent(): PlayerSave {
    if (this.current == null) {
      throw new Error('[SaveManager] getCurrent() wywołane przed load() — wywołaj await SaveManager.load() przy starcie aplikacji.');
    }
    return this.current;
  }

  /**
   * Zapisuje stan. **Klonuje argument** (shallow) — nie mutuje przekazanego obiektu.
   * Ustawia `lastSavedAt` i `version` na klonie, przekazuje do adapter'a.
   *
   * Po sukcesie aktualizuje cached `current`. Jeśli zapis się nie uda
   * (np. quota exceeded), cached `current` POZOSTAJE STARY — caller dostaje
   * Promise reject i decyduje co z tym zrobić. Brak silent data loss.
   *
   * W trybie `inMemoryOnly` save() aktualizuje tylko cached (skip disk write).
   */
  async save(state: PlayerSave): Promise<void> {
    // Shallow clone — top-level pola są primitives/refs które chcemy zachować.
    // Nested objects (currencies, inventory) nie są mutowane w `save()`, shared OK.
    const snapshot: PlayerSave = {
      ...state,
      lastSavedAt: Date.now(),
      version: SAVE_VERSION,
    };

    if (this.inMemoryOnly) {
      this.current = snapshot;
      return;
    }

    try {
      await this.adapter.save(snapshot);
      this.current = snapshot;
    } catch (err) {
      if (err instanceof StorageUnavailableError) {
        // **Runtime storage failure** (np. quota exceeded mid-game, browser
        // unmount IDB). Degraduje do in-memory mode — cached aktualizujemy,
        // caller dostaje sukces, gracz może kontynuować zabawę bez disk persistence.
        // Bez tego modale (avatar selector, sklep) zawieszają się czekając na save.
        console.warn('[SaveManager] Runtime storage error, przełączam w in-memory:', err.message);
        this.inMemoryOnly = true;
        this.current = snapshot;
        return;
      }
      throw err;     // inne błędy propagują
    }
  }

  /**
   * **Atomic update (recipe pattern):** kolejne wywołania są serializowane przez
   * `updateLock`. Recipe (funkcja `current → next`) jest wywoływana DOPIERO gdy
   * poprzedni `update()` się skończył — czyli `current` w argumencie jest świeży.
   *
   * Eliminuje race window gdy dwa miejsca (np. AutoSaveBridge + Modal) budują
   * snapshot równolegle z OUTDATED `getCurrent()` — drugi by nadpisał pierwszy.
   *
   * Preferowany pattern dla wszystkich update'ów stanu: AutoSaveBridge, modale,
   * future settings, daily reward triggery itp.
   *
   * Codex review (sesja 18) — wskazał potrzebę tego po przeglądzie modal vs autosave race.
   */
  private updateLock: Promise<void> = Promise.resolve();

  async update(recipe: (current: PlayerSave) => PlayerSave): Promise<void> {
    const next = this.updateLock.catch(() => {}).then(async () => {
      const current = this.getCurrent();
      const updated = recipe(current);
      await this.save(updated);
    });
    this.updateLock = next;
    return next;
  }

  /**
   * **Adopcja autorytatywnego stanu z serwera (sesja 35B).** Granularne
   * endpointy ekonomii (`/shop/buy-egg`, `/eggs/:uid/hatch`, `/upgrades/...`,
   * `/biomes/.../unlock`, `/starter-pets/choose`) zwracają pełny PlayerSave po
   * mutacji. Ten save jest JUŻ zapisany w Postgres — robienie blob PUT-a
   * zdublowałoby go (i mogłoby cofnąć przez race z autosave). `adoptServerState`
   * ustawia tylko cached `current`, BEZ zapisu na dysk.
   *
   * Idzie przez `updateLock` — jeśli autosave `update()` jest w locie, czekamy
   * aż skończy, dopiero potem nadpisujemy `current`. Bez tego kończący się
   * autosave nadpisałby świeży server-state starym snapshotem.
   */
  async adoptServerState(state: PlayerSave, saveRev?: number): Promise<boolean> {
    let applied = false;
    const next = this.updateLock.catch(() => {}).then(() => {
      if (saveRev != null && saveRev <= this.appliedRev) {
        // Stara / spóźniona / zduplikowana odpowiedź — nowszy stan już
        // zastosowany. NIE cofamy `current` (drift-guard, chroni waluty/staty).
        console.debug(
          `[SaveManager] pomijam server-state (save_rev ${saveRev} <= applied ${this.appliedRev})`,
        );
        return;
      }
      this.maybeWarnRevGap(saveRev);
      this.current = state;
      if (saveRev != null) this.appliedRev = saveRev;
      applied = true;
    });
    this.updateLock = next;
    await next;
    // Zwraca czy payload zastosowano — caller synchronizuje store/HUD TYLKO gdy
    // `true` (HIGH 1: inaczej store cofnąłby waluty/inventory mimo skipu `current`).
    return applied;
  }

  /**
   * **Patch walut/statystyk z `/world/tick` (Faza 1a — dieta payloadu).**
   * Od Fazy 1a world tick NIE odsyła już całego save'a (tylko waluty + staty),
   * bo nigdy nie zmienia petów/jajek. Zamiast `adoptServerState` (które
   * zastąpiłoby cały `current`, w tym ekwipunek) patchujemy WYŁĄCZNIE
   * `currencies` + `stats` — `inventory`/`eggs` pozostają nietknięte.
   *
   * Bez zapisu na dysk (jak `adoptServerState`) — coins/gems są
   * server-authoritative (kredytowane przez `/world/tick`), nie przez PUT.
   * Idzie przez `updateLock`, żeby nie wyścigać się z trwającym `update()`.
   */
  async patchServerScalars(
    fields: {
      currencies: PlayerSave['currencies'];
      stats: PlayerSave['stats'];
      /** EV-1b-2: portfel walut eventowych (server-authoritative, z /world/tick). */
      eventCurrencies?: PlayerSave['eventCurrencies'];
    },
    saveRev?: number,
  ): Promise<boolean> {
    let applied = false;
    const next = this.updateLock.catch(() => {}).then(() => {
      if (this.current == null) return;
      if (saveRev != null && saveRev <= this.appliedRev) {
        // Spóźniony / zduplikowany world-tick — nie cofaj walut (drift-guard).
        console.debug(
          `[SaveManager] pomijam patch skalarów (save_rev ${saveRev} <= applied ${this.appliedRev})`,
        );
        return;
      }
      this.maybeWarnRevGap(saveRev);
      this.current = {
        ...this.current,
        currencies: { ...this.current.currencies, ...fields.currencies },
        stats: { ...this.current.stats, ...fields.stats },
        // EV-1b-2: portfel eventowy server-authoritative (REPLACE pełną mapą z
        // serwera, jak gems) — gdy tick go zwrócił. Brak pola = bez zmian.
        eventCurrencies: fields.eventCurrencies ?? this.current.eventCurrencies,
      };
      if (saveRev != null) this.appliedRev = saveRev;
      applied = true;
    });
    this.updateLock = next;
    await next;
    // Czy patch zastosowano — caller (AutoSaveBridge) ustawia HUD coins/gems
    // TYLKO gdy `true` (HIGH 1: stary tick nie może nadpisać HUD przez boczną ścieżkę).
    return applied;
  }

  /**
   * **Aplikacja delty akcji ekonomii (Faza 1b-2, DESIGN_PERFORMANCE_SCALING.md
   * §A2).** `mutator` dostaje `current` i zwraca nowy (immutable add/remove
   * petów/jajek + waluty/staty/equipped). Drift-guard po `save_rev`:
   *  - `saveRev === appliedRev+1` → mutuj `current`, `applied=true`.
   *  - `saveRev > appliedRev+1` → LUKA (np. multi-tab) → `gap=true`, NIE mutuj
   *    (delty nie wolno aplikować przyrostowo gdy brakuje stanu pośredniego);
   *    caller robi pełny resync `GET /saves/me`.
   *  - `saveRev <= appliedRev` → stale/duplikat → skip.
   * Wszystko pod `updateLock` (serializacja z `update`/`adopt`/`patch`).
   */
  async applyDelta(
    mutator: (current: PlayerSave) => PlayerSave,
    saveRev: number,
  ): Promise<{ applied: boolean; gap: boolean }> {
    let applied = false;
    let gap = false;
    const next = this.updateLock.catch(() => {}).then(() => {
      if (this.current == null) return;
      if (saveRev <= this.appliedRev) {
        console.debug(`[SaveManager] pomijam deltę (save_rev ${saveRev} <= applied ${this.appliedRev})`);
        return;
      }
      if (saveRev > this.appliedRev + 1) {
        console.debug(`[SaveManager] luka save_rev ${this.appliedRev}→${saveRev} — delta wymaga resync`);
        gap = true;
        return;
      }
      this.current = mutator(this.current);
      this.appliedRev = saveRev;
      applied = true;
    });
    this.updateLock = next;
    await next;
    return { applied, gap };
  }

  /**
   * Drift-guard (Faza 1b-1): ustaw bazowy `appliedRev`. Przeznaczone dla boot
   * (inicjalizacja z `GET /saves/me`); obecnie guard samoinicjuje się na
   * pierwszej zaadoptowanej odpowiedzi, ale metoda jest gotowa pod jawny boot-init.
   */
  setAppliedRev(rev: number): void {
    this.appliedRev = rev;
  }

  /** Aktualny `appliedRev` (drift-guard) — do testów/diagnostyki. */
  getAppliedRev(): number {
    return this.appliedRev;
  }

  /**
   * Luka w `save_rev` (skok > 1, niegenerowany lokalnie — typowo inna karta
   * zmutowała save). W tej rundzie odpowiedzi niosą PEŁNY save, więc adopcja
   * jest samonaprawialna (przyjmujemy najnowszy stan). Jawny resync
   * `GET /saves/me` przy luce dojdzie w rundzie delt, gdzie zgubiona delta =
   * realna korupcja. Tu tylko log diagnostyczny.
   */
  private maybeWarnRevGap(saveRev: number | undefined): void {
    if (saveRev != null && this.appliedRev > 0 && saveRev > this.appliedRev + 1) {
      console.debug(
        `[SaveManager] luka save_rev: applied ${this.appliedRev} → ${saveRev} (multi-tab? pełny save samonaprawia)`,
      );
    }
  }

  /**
   * Usuwa save z persistence (deleguje do adaptera) i resetuje cached state.
   * Po `clear()`:
   *  - `getCurrent()` rzuca jak przed `load()` — caller MUSI zrobić full
   *    reload (lokacja albo ponowne `load()`)
   *  - następne `load()` zobaczy "brak save'a" → fresh save
   *
   * Sesja 23c: facada nad `adapter.clear()` żeby dev helpers nie sięgali do
   * `saveManager['adapter']` (private). Idzie przez `updateLock` — jeśli
   * jakiś `update()` jest w locie (np. autosave flush), poczekamy aż skończy,
   * potem clear. Bez tego clear mógłby zostać "nadpisany" przez kończące się
   * update które miało stary snapshot.
   */
  async clear(): Promise<void> {
    const next = this.updateLock.catch(() => {}).then(async () => {
      await this.adapter.clear();
      this.current = null;
      // Reset drift-guard — nowy/zresetowany save zaczyna od rev 0 (samoinicjuje
      // się na pierwszej odpowiedzi serwera nowego konta).
      this.appliedRev = 0;
    });
    this.updateLock = next;
    return next;
  }

  /** Serializuje aktualny save do JSON-stringa (do ręcznego backupu gracza). */
  exportJson(): string {
    if (this.current == null) {
      throw new Error('[SaveManager] exportJson() przed load()');
    }
    return JSON.stringify(this.current, null, 2);
  }

  /**
   * Importuje save z JSON-stringa.
   * Robi minimalną walidację + migracje. Throws przy uszkodzonym JSON.
   */
  async importJson(json: string): Promise<void> {
    const parsed = JSON.parse(json) as Record<string, unknown>;
    const migrated = applyMigrations(parsed);
    await this.save(migrated);
    console.info('[SaveManager] Zaimportowano save z JSON');
  }
}
